import interact from 'interactjs';
import {ITemplateField, IEnvelopeField} from '@verdocs/js-sdk';
import {Component, Event, EventEmitter, Fragment, h, Host, Method, Prop, State, Element, Listen, Watch} from '@stencil/core';
import {SettingsIcon} from '../../../utils/Icons';
import {Store} from '../../../utils/Datastore';

/**
 * Displays a checkbox.
 */
@Component({
  tag: 'verdocs-field-checkbox',
  styleUrl: 'verdocs-field-checkbox.scss',
  shadow: false,
})
export class VerdocsFieldCheckbox {
  @Element() el: HTMLElement;

  /**
   * Fields may be attached to templates or envelopes, but only template fields may be edited.
   */
  @Prop({reflect: true}) source: 'template' | 'envelope' = 'template';

  /**
   * The source template or envelope ID the field is found in.
   */
  @Prop({reflect: true}) sourceid: string = '';

  /**
   * The name of the field to display.
   */
  @Prop({reflect: true}) fieldname: string = '';

  /**
   * Override the field's settings. This is intended to be used during signing when fields are being
   * mutated.
   */
  @Prop() field: IEnvelopeField | null | undefined = null;

  /**
   * If set to true, it will force interact to unset the el, resulting in no dragging the field.
   */
  @Prop({reflect: true}) isPreview?: boolean;

  /**
   * If set, overrides the field's settings object. Primarily used to support "preview" modes where all fields are disabled.
   */
  @Prop({reflect: true}) disabled?: boolean = false;

  /**
   * If set, the field is considered "done" and is drawn in a display-final-value state.
   */
  @Prop({reflect: true}) done?: boolean = false;

  /**
   * If set, a settings icon will be displayed on hover. The settings shown allow the field's recipient and other settings to be
   * changed, so it should typically only be enabled in the Builder.
   */
  @Prop({reflect: true}) editable?: boolean = false;

  /**
   * If set, the field may be dragged to a new location. This should only be enabled in the Builder, or for self-placed fields.
   */
  @Prop({reflect: true}) moveable?: boolean = false;

  /**
   * If set, the field will be be scaled horizontally by this factor.
   */
  @Prop({reflect: true}) xscale?: number = 1;

  /**
   * If set, the field will be be scaled vertically by this factor.
   */
  @Prop({reflect: true}) yscale?: number = 1;

  /**
   * The page the field is on
   */
  @Prop({reflect: true}) pagenumber?: number = 1;

  /**
   * Event fired when the field's settings are changed.
   */
  @Event({composed: true}) settingsChanged: EventEmitter<{fieldName: string; field: ITemplateField}>;

  /**
   * Event fired when the field is deleted.
   */
  @Event({composed: true}) deleted: EventEmitter<{fieldName: string}>;

  @State() showingProperties?: boolean = false;
  @State() focused = false;

  @Listen('blur')
  handleBlur() {
    this.focused = false;
  }

  @Method()
  async focusField() {
    this.el.focus();
    this.focused = true;
  }

  @Method()
  async showSettingsPanel() {
    const settingsPanel = document.getElementById(`verdocs-settings-panel-${this.fieldname}`) as any;
    if (settingsPanel && settingsPanel.showPanel) {
      settingsPanel.showPanel();
    }
  }

  @Method()
  async hideSettingsPanel() {
    const settingsPanel = document.getElementById(`verdocs-settings-panel-${this.fieldname}`) as any;
    if (settingsPanel && settingsPanel.hidePanel) {
      settingsPanel.hidePanel();
    }
  }

  @Watch('editable')
  onEditableChanged(newVal: boolean, oldVal: boolean) {
    // When transitioning out of editable mode (e.g., builder -> preview tab), clear interact bindings
    if (oldVal && !newVal) {
      interact(this.el).unset();
    }
  }

  disconnectedCallback() {
    // Clear any interact.js drag/resize bindings so they don't leak if the DOM element is reused elsewhere
    interact(this.el).unset();
  }

  render() {
    const {source, sourceid, fieldname, editable = false, done = false, disabled = false, focused, xscale = 1, yscale = 1} = this;
    const {index, field} = Store.getField(source, sourceid, fieldname, this.field);
    const {required = false, label = '', value = false, readonly = false} = field || {};
    const signerClass = `signer-${(index % 10) + 1}`;

    const checked = value === 'true';

    if (done) {
      return <Host class={{'verdocs-field': true,done}}>{checked ? '✓' : '☐'}</Host>;
    }

    return (
      <Host class={{'verdocs-field': true,required, disabled, done, focused, [signerClass]: true}}>
        {label && <div class="label">{label}</div>}

        <label htmlFor={fieldname}>
          <input id={fieldname} name={fieldname} type="checkbox" checked={checked} disabled={readonly || disabled} required={required} />
          <span />

          {editable && (
            <Fragment>
              <div
                id={`verdocs-settings-panel-trigger-${fieldname}`}
                style={{transform: `scale(${Math.floor((1 / xscale) * 1000) / 1000}, ${Math.floor((1 / yscale) * 1000) / 1000})`}}
                class="settings-icon"
                innerHTML={SettingsIcon}
                onClick={(e: any) => {
                  e.stopPropagation();
                  this.showingProperties = !this.showingProperties;
                }}
              />

              {this.showingProperties && (
                <verdocs-portal anchor={`verdocs-settings-panel-trigger-${field.name}`} onClickAway={() => (this.showingProperties = false)}>
                  <verdocs-template-field-properties
                    templateId={sourceid}
                    fieldName={field.name}
                    onClose={() => (this.showingProperties = false)}
                    onDelete={() => {
                      this.deleted?.emit({fieldName: fieldname});
                      return this.hideSettingsPanel();
                    }}
                    onSettingsChanged={e => {
                      this.settingsChanged?.emit(e.detail);
                      return this.hideSettingsPanel();
                    }}
                    helpText={'Check boxes allow the user to select one or more (non-exclusive) options.'}
                  />
                </verdocs-portal>
              )}
            </Fragment>
          )}
        </label>
      </Host>
    );
  }
}
